import { Box, Dialog, Grid, Collapse, IconButton, useTheme, useMediaQuery, Typography } from "@mui/material";
import Header from "../../common/Header";
import CustomTextField from "../../common/CustomTextField";
import {
  AssignmentReturnedOutlined,
  AttachMoney,
  CloseOutlined,
  CloudDownload,
  CreditCard,
  CurrencyExchangeOutlined,
  DownloadOutlined,
  EditNote,
  EditOutlined,
  ExitToApp,
  GridOn,
  HistoryOutlined,
  KeyboardArrowDown,
  ListAltOutlined,
  ModeOutlined,
  PaymentsOutlined,
  SearchOutlined,
  TrackChanges,
} from "@mui/icons-material";
import Card from "../../common/Card";
import { useForm } from "react-hook-form";
import Button from "../../common/Button";
import Table, { Row } from "../../common/CustomTable";
import { useLocation } from "react-router-dom";
import {
  SearchInvoiceInfo,
  addLateFeeByInvoice,
  changeInvoiceStatus,
  fetchInvoiceList,
  invoiceInfo,
  resetInvoiceList,
  stripeCheckOutPaymentData,
  useInvoiceSlice,
} from "../../../redux/slice/invoice/InvoiceSlice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AgencyInfo,
  fetchAgenciesList,
  setSingleAlertObj,
  showHideLoadingDialog,
  SingleAlertInfo,
  useCommonReducer,
} from "../../../redux/slice/commonSlice";
import CustomMultipleDropDown from "../../common/CustomMultipleDropDown";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  fetchDocumentById,
  getPracticeListforFilter,
  usePracticeSlice,
} from "../../../redux/slice/practiceSlice";
import {
  selectIsValueFoundInToken,
  useAuthSlice,
} from "../../../redux/slice/authSlice";
import { AUDIT_ENTITYS, CAN_ACCESS_APPLICATION, COMMISSION_STATUS, EXPORTABLE_ITEMS, INVOICE_ACTION, INVOICE_STATUS, USER_ROLE_ENTITY_NAMES } from "../../../constants/applicationConstants";
import { UserRole, fetchAllUserRoles, fetchUserPreference, saveUserPreference } from "../../../redux/slice/userSlice";
import dayjs from "dayjs";
import CustomCheckbox from "../../common/CustomCheckBox";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { fullName, hideElement, priceFormatter, sortNames } from "../../../utils/Utility";
import { canShowUpdateInvoice } from "../../../utils/invoiceUpdateVisibility";
import { createListSort } from "../../../utils/listSort";
import { asEntityNumber, buildActivityCenterPath } from "../../../utils/activityCenterUtils";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import CustomIconButton from "../../common/CustomIconButton";
import CustomDateRange from "../../common/CustomDateRange";
import { getSearchCriteriaFromSessionStorage, saveSelectionForPage, setSearchCriteriaToSessionStorage } from "../../../utils/StorageUtils";
import { DateObject } from "react-multi-date-picker";
import { tokens } from "../../../theme";
import ExportToExcel from "../../common/ExportToExcel";
import CustomMonthYearPicker from "../../common/CustomMonthYearPicker";
import { format, isValid, parse } from "date-fns";
import { useNavigation } from "../../../hooks/useNavigation";
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
import StyledMenu from "../../common/StyledMenu";
import {  getPatientListForFilter, usePatientReducer } from "../../../redux/slice/patient/patientSlice";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { useBranding } from "../../../BrandingContext";
import { fetchProductsForProductsSearch, useProductListReducer, ProductInfo } from "../../../redux/slice/product/productListSlice";
import UploadPaymentsPopup from "../invoice/UploadPaymentsPopup";
import { fetchManufacturerListByRole, useManufacturerListSlice } from "../../../redux/slice/manufacturer/ManufacturerSlice";
import { useModalStyles } from "../../common/useModalStyles";
import { useListPageStyles } from "../../common/useListPageStyles";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import LateFeePopup from "../invoice/LateFeePopup";
import PDFViewer from "../../common/pdf/PDFViewer";
import StripePaymentTypePopup from "../payment/stripe/StripePaymentTypePopup";
import { OverridePayoutInvoice, RecordPayoutOverrideForm } from "../invoice/RecordPayoutOverride";
import { AgencyPayoutInvoice, RecordPayoutForm } from "../invoice/RecordPayout";
import EditCommissionPopup from "../orders/EditCommissionPopup";
import {
  invoiceHasServiceFeePayment,
  resolveServiceFeeAmountEdit,
  saveServiceFeeAmountEdit,
  ServiceFeeAmountEditTarget,
  ServiceFeeKind,
} from "../../../utils/serviceFeeAmountEdit";

interface Column {
  id: string;
  label: string | JSX.Element;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
  padding?: string;
}

interface invoiceTableRow extends invoiceInfo {
  checkBox: JSX.Element | any;
  rawMfgInvoiceNumber?: string | number | null; // CS-1517 - For custom sorting
  actualRemainingAmount?: number | null;
}

const commissionListSort = createListSort(
  ["mfgInvoiceNumber", "mfgInvoiceDate", "invoiceDueDate", "manufacturerName", "practiceName", "orderNumber", "invoiceAmount", "invoiceStatus"] as const,
  {
    mfgInvoiceNumber: "mfgInvoiceNumber",
    mfgInvoiceDate: "mfgInvoiceDate",
    invoiceDueDate: "invoiceDueDate",
    manufacturerName: "manufacturerName",
    practiceName: "practiceName",
    orderNumber: "orderNumber",
    invoiceAmount: "invoiceAmount",
    invoiceStatus: "invoiceStatus",
  }
);

const CommissionList = () => {
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const { control, handleSubmit, reset, setValue, getValues, watch } =
    useForm<SearchInvoiceInfo>();
  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const branding = useBranding();
  const isDark = theme.palette.mode === "dark";
  const { primaryBtnSx, tokens: modalTokens, paperSx, headerSx, iconBoxSx, bodySx, closeIconSx } = useModalStyles();
  const { filterPanelSx } = useListPageStyles();

  const legacyItems = [{ icon: <ModeOutlined color="info" />, text: "Edit" }];
  const { agencyInfoList } = useCommonReducer();
  const { manufacturerInfoListByUserRole } = useManufacturerListSlice();
  const { practiceByUserRole } = usePracticeSlice();
  const { userInfo } = useAuthSlice();
  const { patientsByUserRole } = usePatientReducer();
  const { invoiceList } = useInvoiceSlice();
  const { productsByUserRole } = useProductListReducer();
  const [showCard, setShowCard] = useState(false);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [showUploadPaymentsPopup, setShowUploadsPaymentsPopup] =
    useState(false);
  const [selectedAgencyId, setSelectedAgencyId] = useState<number | null>(null);
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);

  const [isDateRangeChanged, setIsDateRangeChanged] = useState(false);
  const [isClearDateRange, setIsClearDateRange] = useState(false);

  const [isV3DateRangeChanged, setIsV3DateRangeChanged] = useState(false);
  const [isV3ClearDateRange, setIsV3ClearDateRange] = useState(false);

  const [isAgencyDateRangeChanged, setIsAgencyDateRangeChanged] = useState(false);
  const [isAgencyClearDateRange, setIsAgencyClearDateRange] = useState(false);

  const [isOvrDateRangeChanged, setIsOvrDateRangeChanged] = useState(false);
  const [isOvrClearDateRange, setIsOvrClearDateRange] = useState(false);

  const ITEMS_PER_PAGE = 10;

  const selectedMfgIds = watch('mfgIds') || [];
  const selectedPracticeIds = (watch('practiceIds') || []) as number[];
  const productOptions = useMemo(() => {
    let options: { label: string; value: number }[] = [];
    if (productsByUserRole && productsByUserRole.length > 0) {
      const filteredByMfg = selectedMfgIds && selectedMfgIds.length > 0
        ? productsByUserRole.filter((product) => selectedMfgIds.includes(Number(product.manufacturerId)))
        : productsByUserRole;
      const sortedProductList: ProductInfo[] = sortNames(filteredByMfg, "productName");
      sortedProductList?.forEach((option) => {
        const isInactive = option.inactiveDate && dayjs(option.inactiveDate).isBefore(dayjs(), "day");
        options.push({
          label: isInactive ? `${option.productName} (INACTIVE)` : option.productName,
          value: Number(option.productId),
        });
      });
    }
    return options;
  }, [productsByUserRole, selectedMfgIds]);

  useEffect(() => {
    const currentProductIds = (getValues("productIds") || []) as number[];
    if (currentProductIds.length > 0) {
      const validProductIds = currentProductIds.filter((id) =>
        productOptions.some((option) => option.value === Number(id))
      );
      if (validProductIds.length !== currentProductIds.length) {
        setValue("productIds", validProductIds);
      }
    }
  }, [productOptions]);

  const canAccessRecordCreditMemoAction = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.CREDIT_MEMO)(state));
  const canAccessAddPaymentAction = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.ADD_PAYMENT)(state));
  const canAccessEditInvoiceAction = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.EDIT_INVOICE)(state));
  const canCreateStripePayment = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.STRIPE_PAYMENT)(state));
  const canAccessCloseInvoiceAction = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.CLOSE_INVOICE)(state));
  const canAuditInvoice = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessAuditAction", AUDIT_ENTITYS.INVOICEAUDIT)(state));
  const canLateFeeAction = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.LATE_FEE)(state));
  const canAccessActivityCenter = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.ACTIVITY_CENTER)(state));
  const [userPreferencesLoaded, setUserPreferencesLoaded] = useState(false);
  const [activeButtonId, setActiveButtonId] = useState<string | null>(null);
  const [showOptionPopup, setShowOptionsPopup] = useState<OptionPopupObjectProps>({ message: "", show: false });
  const [selectedInvoiceData, setSelectedInvoiceData] = useState<any>(null);
  const [showStripePaymentTypePopup, setShowStripePaymentTypePopup] = useState(false);
  const [overridePayoutInvoices, setOverridePayoutInvoices] = useState<OverridePayoutInvoice[] | null>(null);
  const [agencyPayoutInvoices, setAgencyPayoutInvoices] = useState<AgencyPayoutInvoice[] | null>(null);
  const [serviceFeeEdit, setServiceFeeEdit] = useState<ServiceFeeAmountEditTarget | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const [lateFeeAmount, setLateFeeAmount] = useState<number>(0);
  const [pdfContent, setPDFContent] = useState<string>("");
  const [showPDF, setShowPDF] = useState<{
    isShow: boolean;
    noteName: string;
    fileName: string;
  }>({ isShow: false, noteName: "", fileName: "" });

  const canAccessUploadCommisions = useSelector((state: RootState) => selectIsValueFoundInToken("canUploadPayments", INVOICE_STATUS.COMMISSION)(state));
  const canAccessCreateAGYPayout = useSelector((state: RootState) => selectIsValueFoundInToken("canCreatePayOut", INVOICE_STATUS.AGENCY)(state));
  const canAccessCreateOVRAGYPayout = useSelector((state: RootState) => selectIsValueFoundInToken("canCreatePayOut", INVOICE_STATUS.OVERRIDE)(state));
  const canEditCommissionRate = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessCommissionActions", COMMISSION_STATUS.EDIT)(state));
  const canExportCommissionList = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessExportExcel", EXPORTABLE_ITEMS.COMMISSION)(state));
  // Restore activeButtonId from sessionStorage on mount
  useEffect(() => {
    dispatch(showHideLoadingDialog(true));
    const saved = sessionStorage.getItem("activeActionButtonId");
    if (saved) {
      setActiveButtonId(saved);
    }
  }, []);

// CS-1300 - START
  // Only fetch user preferences once on mount
  useEffect(() => {
  let isMounted = true;

  const fetchInitialData = async () => {
    try {
      const [
        agenciesResult,
        preferencesResult,
        practicesResult,
        patientsResult
      ] = await Promise.all([
        dispatch(fetchManufacturerListByRole({ skipLoader: false })),
        dispatch(fetchAgenciesList()),
        dispatch(fetchUserPreference(userInfo.userId)),
        dispatch(getPracticeListforFilter()),
        dispatch(getPatientListForFilter()),
        dispatch(fetchProductsForProductsSearch(false))
      ]);

      if (!isMounted) return;

      const preferences = preferencesResult.payload || [];
      const preferredColumns = preferences.find(
        (pref: { preferenceName: string }) =>
          pref.preferenceName === "preferredInvoiceListColumns"
      );

      if (preferredColumns) {
        setHiddenColumns(preferredColumns.preferenceValue.split(";"));
      } else if (userInfo.userRole === "ROLE_ADMIN") {
        setHiddenColumns([
          "agencyName",
          "agencyCommissionAmount",
          "agencyCommissionPaidAmount",
          "overrideAgencyName",
          "ovrAgencyCommissionAmount",
          "ovrAgencyCommissionPaidAmount",
        ]);
      }

      setUserPreferencesLoaded(true);
    } catch (err) {
      console.error("Error loading initial data", err);
    }
  };

  fetchInitialData();
  return () => {
    isMounted = false;
  };
}, [dispatch, userInfo.userId, userInfo.userRole]);


const prevHiddenColumnsRef = useRef<string[]>([]);

useEffect(() => {
  if (userPreferencesLoaded) {
    const prev = prevHiddenColumnsRef.current;
    const current = hiddenColumns;

    const hasChanged =
      prev.length !== current.length ||
      prev.some((val, i) => val !== current[i]);

    if (hasChanged) {
      const hiddenColumnsString = current.join(";");
      dispatch(
        saveUserPreference({
          userId: userInfo?.userId,
          preferenceName: "preferredInvoiceListColumns",
          preferenceValue: hiddenColumnsString,
        })
      );
      prevHiddenColumnsRef.current = [...current];
    }
  }
}, [hiddenColumns, userPreferencesLoaded, dispatch, userInfo?.userId]);


// Only run searchInvoiceList once after user preferences are loaded
useEffect(() => {
  if (!userPreferencesLoaded) return;

  const {
    orderNumber,
    mfgInvoiceNumber,
    fromDate,
    toDate,
    invoiceListSearchCriteria,
    withSearchHistory,
    v3ReceivedFromDate,
    v3ReceivedToDate,
    agencyPaidFromDate,
    agencyPaidToDate,
    ovrPaidFromDate,
    ovrPaidToDate
  } = location.state || {};

  const criteriaKey = `${CAN_ACCESS_APPLICATION.INVOICE_MANAGEMENT}SearchCriteria${userInfo.userId}`;
  const savedCriteria = getSearchCriteriaFromSessionStorage(criteriaKey);

  if (orderNumber || mfgInvoiceNumber) {
    setShowCard(true);
    if (orderNumber) setValue("orderNumber", orderNumber);
    if (mfgInvoiceNumber) setValue("mfgInvoiceNumber", mfgInvoiceNumber);

    searchInvoiceList(1, {
      ...getValues(),
      orderNumber: orderNumber || "",
      mfgInvoiceNumber: mfgInvoiceNumber || "",
      fromDate: fromDate || "",
      toDate: toDate || "",
      v3ReceivedFromDate: v3ReceivedFromDate || "",
      v3ReceivedToDate: v3ReceivedToDate || "",
      agencyPaidFromDate: agencyPaidFromDate || "",
      agencyPaidToDate: agencyPaidToDate || "",
      ovrPaidFromDate: ovrPaidFromDate || "",
      ovrPaidToDate: ovrPaidToDate || ""
    });

    if (!fromDate && !toDate) setIsClearDateRange(true);
    if (!v3ReceivedFromDate && !v3ReceivedToDate) setIsV3ClearDateRange(true);
    if (!agencyPaidFromDate && !agencyPaidToDate) setIsAgencyClearDateRange(true);
    if (!ovrPaidFromDate && !ovrPaidToDate) setIsOvrClearDateRange(true);
    return;
  }

  if (invoiceListSearchCriteria || (withSearchHistory && savedCriteria)) {
    const criteria = invoiceListSearchCriteria || savedCriteria;
    if (withSearchHistory) {
      navigate("/commission_list", { state: { invoiceListSearchCriteria: criteria }, replace: true }); // force reload
      return;
    }

    setValuesToCriteriaFields(criteria);
    searchInvoiceList(criteria.currentPage || 1, criteria);
    return;
  }

  const localFrom = getValues("fromDate");
  const localTo = getValues("toDate");

  const localV3From = getValues("v3ReceivedFromDate");
  const localV3To = getValues("v3ReceivedToDate");

  const localAgencyFrom = getValues("agencyPaidFromDate");
  const localAgencyTo = getValues("agencyPaidToDate");

  const localOvrFrom = getValues("ovrPaidFromDate");
  const localOvrTo = getValues("ovrPaidToDate");

  if ((localFrom && localTo) || (localV3From && localV3To) || (localAgencyFrom && localAgencyTo) || (localOvrFrom && localOvrTo)) {
    searchInvoiceList(1, { ...getValues(), fromDate: localFrom, toDate: localTo, v3ReceivedFromDate: localV3From,
      v3ReceivedToDate: localV3To, agencyPaidFromDate: localAgencyFrom, agencyPaidToDate: localAgencyTo,
      ovrPaidFromDate: localOvrFrom, ovrPaidToDate: localOvrTo });
    if (localFrom && localTo) {
      setIsDateRangeChanged(false);
    }

    if (localV3From && localV3To) {
      setIsV3DateRangeChanged(false);
    }

    if (localAgencyFrom && localAgencyTo) {
      setIsAgencyDateRangeChanged(false);
    }

    if (localOvrFrom && localOvrTo) {
      setIsOvrDateRangeChanged(false)
    }

  } else {
    searchInvoiceList(1);
  }
}, [location.state, practiceByUserRole, manufacturerInfoListByUserRole, userPreferencesLoaded]);

// CS-1300 - END

  const setValuesToCriteriaFields = (searchCriteria: SearchInvoiceInfo) => {
    setValue("mfgInvoiceNumber", searchCriteria.mfgInvoiceNumber);
    setValue("orderNumber", searchCriteria.orderNumber);
    setValue("mfgIds", searchCriteria.mfgIds);
    setValue("practiceIds", searchCriteria.practiceIds);
    setValue("invoiceStatus", searchCriteria.invoiceStatus);
    setValue("currentPage", searchCriteria.currentPage);
    setValue("itemsPerPage", searchCriteria.itemsPerPage);
    const restoredSort = commissionListSort.pick(searchCriteria);
    setValue("sortBy", restoredSort.sortBy);
    setValue("sortOrder", restoredSort.sortOrder);
    setValue("serialNumber", searchCriteria.serialNumber);
    setValue("usageStatus", searchCriteria.usageStatus);
    setValue("patientIds", searchCriteria.patientIds);
    setValue("agencyIds", searchCriteria.agencyIds);
    setValue("productIds", searchCriteria.productIds);
    setValue("commPeriod", searchCriteria.commPeriod);
    if (searchCriteria.comm) {
      setValue("comm", searchCriteria.comm);
    } else if (searchCriteria.commPeriod) {
      const parsedDate = parse(
        searchCriteria.commPeriod.split(" - ")[0].trim(),
        "MMM yy",
        new Date()
      );
      if (isValid(parsedDate)) {
        setValue("comm", parsedDate.toISOString());
      }
    }
    setValue("v3CommStatus", searchCriteria.v3CommStatus);
    setValue("agencyCommStatus", searchCriteria.agencyCommStatus);
    setValue("ovrCommStatus", searchCriteria.ovrCommStatus);
    setValue("v3ReceivedFromDate", searchCriteria.v3ReceivedFromDate);
    setValue("v3ReceivedToDate", searchCriteria.v3ReceivedToDate);
    setValue("agencyPaidFromDate", searchCriteria.agencyPaidFromDate);
    setValue("agencyPaidToDate", searchCriteria.agencyPaidToDate);
    setValue("ovrPaidFromDate", searchCriteria.ovrPaidFromDate);
    setValue("ovrPaidToDate", searchCriteria.ovrPaidToDate);

    if (searchCriteria.fromDate && searchCriteria.toDate) {
      const fromDate = searchCriteria.fromDate || "";
      const toDate = searchCriteria.toDate || "";
      setValue(
        "dateRange",
        fromDate && toDate ? `${fromDate} - ${toDate}` : ""
      );
    } else if (searchCriteria.fromDate) {
      const fromDate = searchCriteria.fromDate || "";
      setValue(
        "dateRange",
        fromDate ? `${fromDate}` : ""
      );
    }

    if (searchCriteria.v3ReceivedFromDate && searchCriteria.v3ReceivedToDate) {
      const v3FromDate = searchCriteria.v3ReceivedFromDate || "";
      const v3ToDate = searchCriteria.v3ReceivedToDate || "";
      setValue("v3ReceivedDateRange",
        v3FromDate && v3ToDate ? `${v3FromDate} - ${v3ToDate}` : ""
      )
    } else if (searchCriteria.v3ReceivedFromDate) {
      const v3FromDate = searchCriteria.v3ReceivedFromDate || "";
      setValue(
        "v3ReceivedDateRange",
        v3FromDate ? `${v3FromDate}` : ""
      );
    }

    if(searchCriteria.agencyPaidFromDate && searchCriteria.agencyPaidToDate){
      const agencyFromDate = searchCriteria.agencyPaidFromDate || "";
      const agencyToDate = searchCriteria.agencyPaidToDate || "";
      setValue("agencyPaidDateRange",
        agencyFromDate && agencyToDate ? `${agencyFromDate} - ${agencyToDate}` : ""
      )
    } else if (searchCriteria.agencyPaidFromDate) {
      const agencyFromDate = searchCriteria.agencyPaidFromDate || "";
      setValue(
        "agencyPaidDateRange",
        agencyFromDate ? `${agencyFromDate}` : ""
      );
    }

     if(searchCriteria.ovrPaidFromDate && searchCriteria.ovrPaidToDate){
      const ovrFromDate = searchCriteria.ovrPaidFromDate || "";
      const ovrToDate = searchCriteria.ovrPaidToDate || "";
      setValue("ovrPaidDateRange",
        ovrFromDate && ovrToDate ? `${ovrFromDate} - ${ovrToDate}` : ""
      )
    } else if (searchCriteria.ovrPaidFromDate) {
      const ovrFromDate = searchCriteria.ovrPaidFromDate || "";
      setValue(
        "ovrPaidDateRange",
        ovrFromDate ? `${ovrFromDate}` : ""
      );
    }


  }

  const invoicesStatusOptions = [
    { label: "PARTIAL", value: "PARTIAL" },
    { label: "PAID", value: "PAID" },
    { label: "UNPAID", value: "UNPAID" },
  ];

  const usageStatusOptions = [
    { label: "AVAILABLE", value: "AVAILABLE" },
    { label: "APPLIED", value: "APPLIED" },
    { label: "RETURN INITIATED", value: "RETURN_INITIATED" },
    { label: "RETURN PENDING", value: "RETURN_PENDING" },
    { label: "RETURNED", value: "RETURNED" }
  ];

  const v3CommissionStatusOptions = [
    { label: "NOT RECEIVED", value: "UNPAID" },
    { label: "RECEIVED", value: "PAID" }
  ];

  const agencyCommissionStatusOptions = [
    { label: "UNPAID", value: "UNPAID" },
    { label: "PAID", value: "PAID" }
  ];

  const patientsOptions = useMemo(() => {
    let options: { label: string; value: string; patientCode: string }[] = [];
    const filteredByPractice = selectedPracticeIds && selectedPracticeIds.length > 0
      ? patientsByUserRole?.filter((p: any) => selectedPracticeIds.includes(Number(p.practiceId)))
      : patientsByUserRole;
    filteredByPractice?.map((option: any) => {
      options.push({
        label: fullName(
          "",
          option.firstName,
          option.middleName,
          option.lastName
        ) + " - " + option.birthDate,
        value: option.patientId,
        patientCode: option.patientCode,
      });
    });
    return options;
  }, [patientsByUserRole, selectedPracticeIds]);

  useEffect(() => {
    const currentPatientIds = getValues("patientIds") || [];
    if (currentPatientIds.length > 0 && selectedPracticeIds.length > 0) {
      const validPatientIds = currentPatientIds.filter((id: any) =>
        patientsByUserRole?.some(
          (p: any) => Number(p.patientId) === Number(id) && selectedPracticeIds.includes(Number(p.practiceId))
        )
      );
      if (validPatientIds.length !== currentPatientIds.length) {
        setValue("patientIds", validPatientIds);
      }
    }
  }, [selectedPracticeIds]);

    const overAgencyListoptions = useMemo(() => {
      let options: { label: string; value: any }[] = [];
      agencyInfoList
        ?.filter(option => option.commissionType === 'Override')
        .map((option) => {
          options.push({
            label: option.agencyName,
            value: option.agencyId,
          });
        });
      return options;
    }, [agencyInfoList]);

  const practiceList = useMemo(() => {
    // CS - 442 START//
    // if (userInfo.userRole === USER_ROLES.ROLE_PRACTICEADMIN) {
    //   let options: { label: string; value: any }[] = [];
    //   if (practiceByUserRole) {
    //     setValue("practiceIds", [Number(practiceByUserRole[0]?.practiceId)]);
    //     practiceByUserRole.map((item) => {
    //       options.push({ label: item.practiceName, value: item.practiceId });
    //     });
    //   }
    //   return options;
    // } else {
    let options: { label: string; value: any }[] = [
      // { label: "ALL", value: "" },
    ];
    //CS - 442 END
    if (practiceByUserRole) {
      if (practiceByUserRole?.length > 1) {
        practiceByUserRole.map((item) => {
          options.push({ label: item.practiceName, value: item.practiceId });
        });
      } else {
        setValue("practiceIds", [Number(practiceByUserRole[0]?.practiceId)]);
        practiceByUserRole.map((item) => {
          options.push({ label: item.practiceName, value: item.practiceId });
        });
      }
    }
    return options;
    // }
  }, [practiceByUserRole]);

  const agenciesList = useMemo(() => {
    let options: { label: string; value: any }[] = [];
    if (agencyInfoList) {
      if(agencyInfoList.length > 1) {
      const sortedAgencies: AgencyInfo[] = sortNames(agencyInfoList, 'agencyName');
      sortedAgencies.map((item) => {
        options.push({
          label: item.agencyName,
          value: item.agencyId,
        });
      });
    } else {
      agencyInfoList.map((item) => {
        options.push({
          label: item.agencyName,
          value: item.agencyId,
        });
      });
      if (
        userInfo.entityName === USER_ROLE_ENTITY_NAMES.AGENCY ||
        userInfo.entityName === USER_ROLE_ENTITY_NAMES.ACCOUNTEXECUTIVE
      ) {
      setValue("agencyIds", [Number(agencyInfoList[0]?.agencyId)]);
      }
    }
    }
    return options;
  }, [agencyInfoList]);

  const manufacturesList = useMemo(() => {
    let options: { label: string; value: any }[] = [];
    if (manufacturerInfoListByUserRole && manufacturerInfoListByUserRole.length > 0) {
      manufacturerInfoListByUserRole.forEach((item) => {
        options.push({
          label: item.manufacturerName,
          value: item.manufacturerId,
        });
      });
      // Fix: Only auto-select if there is exactly one manufacturer and user is MANUFACTURERADMIN or MANUFACTURERSTAFF
      if (
        manufacturerInfoListByUserRole.length === 1 &&
        (userInfo.entityName === USER_ROLE_ENTITY_NAMES.MANUFACTURER)
      ) {
        setValue("mfgIds", [Number(manufacturerInfoListByUserRole[0]?.manufacturerId)]);
      }
    }
    return options;
  }, [manufacturerInfoListByUserRole]);

  const fetchPracticeListByUserRole = async () => {
    const response = await dispatch(fetchAllUserRoles());
    if (response?.meta?.requestStatus === "fulfilled") {
      if (response?.payload) {
        const useRoles = response.payload;
        const role = useRoles?.find(
          (role: UserRole) => role.roleName === userInfo.userRole
        );
        if (role) {
          dispatch(getPracticeListforFilter());
        }
      }
    }
  };

  //CS-1453 Helper for action button click to save highlight state
  const handleActionButtonClick = (buttonId: string, originalClick: () => void) => {
    const currentPageKey = "commissionList";
    saveSelectionForPage(currentPageKey, buttonId);
    setActiveButtonId(buttonId);
    sessionStorage.setItem("activeActionButtonId", buttonId);
    originalClick();
  };

  // CS-1555 - Start
  const handleRowNavigateToDetails = (row: Row) => {
    const invoiceId = row.invoiceId;
    if (!invoiceId) return;
    handleActionButtonClick(`commissionListActionButton-${invoiceId}`, () => {
      const originalRow = invoiceList?.content?.find(r => r.invoiceId === invoiceId);
      navigate("/commission_details", {
        state: {
          invoiceId,
          invoiceAmount: priceFormatter(Number(originalRow?.invoiceAmount)) || (row as any).invoiceAmount,
          InvoiceNumber: originalRow?.mfgInvoiceNumber || (row as any).rawMfgInvoiceNumber || "",
          orderNumber: originalRow?.orderNumber || (row as any).orderNumber || "",
          shippingCharges: originalRow?.shippingCharges || "",
          invStatus: (row as any).invoiceStatus,
          creditAmount: originalRow?.creditAmount || "",
        }
      });
    });
  };
  // CS-1555 - End

  const onSubmit = (data: SearchInvoiceInfo) => {
    if (!isDateRangeChanged) {
      setIsClearDateRange(true);
    }
    const { checkBox, selectAllCheckBox, ...submitObj } = data;
    submitObj.currentPage = 1;
    submitObj.fromDate = isDateRangeChanged ? getValues("fromDate") : "";
    submitObj.toDate = isDateRangeChanged ? getValues("toDate") : "";
    const criteria = commissionListSort.normalize(submitObj);
    navigate("/commission_list", { state: { invoiceListSearchCriteria: criteria } });
  };

  const handleColumnToggleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleColumnToggleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const pagination = (pageNumber: number) => {
    let obj = getValues();
    obj.currentPage = pageNumber + 1;
    navigate("/commission_list", { state: { invoiceListSearchCriteria: obj } });
    setValue('selectAllCheckBox', false);
    formattedInvoiceRows?.map(row => {
      //@ts-ignore
      setValue(`checkBox.invoice_${row.invoiceId}`, false);
    });
    setSelectedAgencyId(null);
  };

  const handleTableSort = (columnId: string, order: "asc" | "desc") => {
    const next = commissionListSort.withSort(getValues(), columnId, order);
    if (!next) {
      return;
    }
    setValue("sortBy", next.sortBy);
    setValue("sortOrder", next.sortOrder);
    setValue("currentPage", 1);
    navigate("/commission_list", { state: { invoiceListSearchCriteria: next } });
  };

  const checkboxComponent = (row: invoiceInfo): JSX.Element => {
    return (
      <Box onClick={(e) => e.stopPropagation()}>
        <CustomCheckbox
          control={control}
          color="primary"
          controllerName={`checkBox.invoice_${row.invoiceId}`}
          // CS-1555 - Start
          onChange={(e) =>
            handleActionButtonClick(`commissionListActionButton-${row.invoiceId}`, () =>
              handleCheckbox(e, row.agencyId)
            )
          }
          // CS-1555 - End
          isDisabled={selectedAgencyId ? selectedAgencyId !== row.agencyId : false}
          // sx={{ p: 0, m: 0 }}
        />
      </Box>
    );
  };

  const handleCheckbox = (
    e: React.ChangeEvent<HTMLInputElement>,
    agencyId: number
  ) => {
    const { checked } = e.target;
    if (!checked) {
      setValue("selectAllCheckBox", false);
      setSelectedAgencyId(null);
    } else {
      setSelectedAgencyId(agencyId);
    }
  };
  // CS-416 Invoice List UI Header modifications
  const columnsInvoice: Column[] = [
    {
      id: "checkBox", label: (
        <CustomCheckbox
          control={control}
          controllerName="selectAllCheckBox"
          isDisabled={!selectedAgencyId}
          onChange={(e) => handleSelectAllCheckBox(e)}
          sx={{ align: "center" }}
        />),
    },
    { id: "mfgInvoiceNumber", label: "INV ID", align: "left" },
    { id: "mfgInvoiceDate", label: "INV Date", align: "left" },
    { id: "invoiceDueDate", label: "INV Due Date", align: "left" },
    { id: "orderNumber", label: "Order ID", align: "left" }, //CS-1141 Changes
    { id: "manufacturerName", label: "MFR", align: "left" },
    { id: "practiceName", label: "Practice", align: "left" },
    { id: "invoiceAmount", label: "INV Amt", align: "right", padding: "0px 80px 0px 0px" },
    { id: "invoiceStatus", label: "Status", align: "left" },
    {id: "action", label: "Action"},
  ];

  const handleClickInvoiceAudit = (row: invoiceInfo) => {
    if (row && row.mfgInvoiceNumber) {
      const invoiceAuditData = {
        entityName: "INVOICE",
        entiyNumber: row.mfgInvoiceNumber,
      };
      const url = `/auditlog?entityName=${invoiceAuditData.entityName}&entityNumber=${invoiceAuditData.entiyNumber}`;
      window.open(url, "_blank");
    }
  };

  const handleCloseInvoice = (row: invoiceInfo) => {
    setShowOptionsPopup({
      message: `Do you really want to close this invoice?`,
      show: true,
      variant: "confirm",
      buttons: [
        {
          name: "Confirm",
          color: "primary",
          onClick: () => handleChangeStatusToClosed(row),
        },
        {
          name: "Cancel",
          color: "secondary",
          onClick: () => setShowOptionsPopup({ message: "", show: false }),
        },
      ],
    });
  };

  const handleChangeStatusToClosed = async (row: invoiceInfo) => {
    const resp = await dispatch(
      changeInvoiceStatus({
        data: {
          invoiceId: row.invoiceId,
          mfgInvoiceStatus: "CLOSED",
        },
        interceptorOption: {},
      })
    );
    if (resp.meta.requestStatus === "fulfilled") {
      const alertObj: SingleAlertInfo = {
        message: `Invoice ${row.mfgInvoiceNumber} closed successfully`,
        alertType: "success",
      };
      dispatch(setSingleAlertObj(alertObj));
      searchInvoiceList(1, { ...getValues() });
    } else {
      const alertObj: SingleAlertInfo = {
        message: "Something went wrong",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
    }
    setShowOptionsPopup({ message: "", show: false });
  };

  const handleCheckout = async (invoiceId: number, paymentMethodType: string, paymentType: string, paymentAmount: number) => {
    const data = {
      returnPath: location.pathname + location.search,
      invoiceId: invoiceId,
      paymentMethod: paymentMethodType,
      invoiceLineItemList: [
        {
          invoiceId: invoiceId,
          paymentAmt: paymentAmount,
          paymentType: paymentType,
        }
      ],
    };
    await dispatch(stripeCheckOutPaymentData(data)).then((res) => {
      if (res && res?.payload) {
        if (res.payload.errors && res.payload.errors.length > 0) {
          const errorMessage = res.payload.errors[0].errorMessage;
          const alertObj: SingleAlertInfo = {
            message: errorMessage,
            alertType: "error",
          };
          dispatch(setSingleAlertObj(alertObj));
        } else if (res.payload.data) {
          const checkoutUrl = res.payload.data.checkoutUrl;
          if (checkoutUrl) window.location.href = checkoutUrl;
        }
      }
    }).catch((error) => {
      console.log(error);
    });
  };

  const handleClickLateFee = (row: invoiceInfo) => {
    setSelectedInvoiceId(row.invoiceId);
    setIsPopupOpen(true);
    setLateFeeAmount(row?.lateFee ?? 0);
  };

  const toOverridePayoutInvoice = (invoice: invoiceInfo): OverridePayoutInvoice => ({
    invoiceId: Number(invoice.invoiceId),
    mfgInvoiceNumber: String(invoice.mfgInvoiceNumber ?? ""),
    mfgInvoiceDate: String(invoice.mfgInvoiceDate ?? ""),
    invoiceAmount: Number(invoice.invoiceAmount) || 0,
    invoiceStatus: String(invoice.invoiceStatus ?? ""),
    overrideAgencyName: invoice.overrideAgencyName ? String(invoice.overrideAgencyName) : undefined,
  });

  const invoiceHasOverridePayout = (row: invoiceTableRow) => {
    const source = invoiceList?.content?.find((invoice) => invoice.invoiceId === row.invoiceId) ?? row;
    return Boolean(
      source.overrideAgencyId ||
      (source.overrideAgencyName && String(source.overrideAgencyName).trim()) ||
      Number(source.expectedOvrAmt) > 0 ||
      Number(source.ovrAgencyCommissionAmount) > 0 ||
      Number(source.ovrAgencyCommissionPaidAmount) > 0
    );
  };

  const openOverridePayout = (row: invoiceTableRow) => {
    const clicked = invoiceList?.content?.find((invoice) => invoice.invoiceId === row.invoiceId);
    const checkedIds = Object.entries(getValues("checkBox") || {})
      .filter(([, isChecked]) => isChecked)
      .map(([key]) => {
        const match = key.match(/invoice_(\d+)/);
        return match ? Number(match[1]) : NaN;
      })
      .filter((id) => Number.isFinite(id));
    const selectedIds = checkedIds.length > 0 ? checkedIds : [row.invoiceId];
    const selected = selectedIds
      .map((id) => invoiceList?.content?.find((invoice) => invoice.invoiceId === id))
      .filter((invoice): invoice is invoiceInfo => Boolean(invoice));
    const invoices = (selected.length > 0 ? selected : clicked ? [clicked] : []).map(toOverridePayoutInvoice);
    if (invoices.length === 0) {
      dispatch(setSingleAlertObj({
        message: "Please select at least one invoice.",
        alertType: "error",
      }));
      return;
    }
    setOverridePayoutInvoices(invoices);
  };

  const handleCloseOverridePayout = () => setOverridePayoutInvoices(null);

  const handleOverridePayoutRecorded = () => {
    setOverridePayoutInvoices(null);
    searchInvoiceList(getValues("currentPage") || 1, { ...getValues() });
  };

  const toAgencyPayoutInvoice = (invoice: invoiceInfo): AgencyPayoutInvoice => ({
    invoiceId: Number(invoice.invoiceId),
    mfgInvoiceNumber: String(invoice.mfgInvoiceNumber ?? ""),
    mfgInvoiceDate: String(invoice.mfgInvoiceDate ?? ""),
    invoiceAmount: Number(invoice.invoiceAmount) || 0,
    invoiceStatus: String(invoice.invoiceStatus ?? ""),
    agencyName: invoice.agencyName ? String(invoice.agencyName) : undefined,
  });

  const invoiceHasAgencyPayout = (row: invoiceTableRow) => {
    const source = invoiceList?.content?.find((invoice) => invoice.invoiceId === row.invoiceId) ?? row;
    return Boolean(
      source.agencyId ||
      (source.agencyName && String(source.agencyName).trim()) ||
      Number(source.expectedAgencyAmt) > 0 ||
      Number(source.agencyCommissionAmount) > 0 ||
      Number(source.agencyCommissionPaidAmount) > 0
    );
  };

  const invoiceCanEditServiceFee = (row: invoiceTableRow) => {
    const source = invoiceList?.content?.find((invoice) => invoice.invoiceId === row.invoiceId) ?? row;
    return invoiceHasServiceFeePayment(source);
  };

  const openAgencyPayout = (row: invoiceTableRow) => {
    const clicked = invoiceList?.content?.find((invoice) => invoice.invoiceId === row.invoiceId);
    const checkedIds = Object.entries(getValues("checkBox") || {})
      .filter(([, isChecked]) => isChecked)
      .map(([key]) => {
        const match = key.match(/invoice_(\d+)/);
        return match ? Number(match[1]) : NaN;
      })
      .filter((id) => Number.isFinite(id));
    const selectedIds = checkedIds.length > 0 ? checkedIds : [row.invoiceId];
    const selected = selectedIds
      .map((id) => invoiceList?.content?.find((invoice) => invoice.invoiceId === id))
      .filter((invoice): invoice is invoiceInfo => Boolean(invoice));
    const invoices = (selected.length > 0 ? selected : clicked ? [clicked] : []).map(toAgencyPayoutInvoice);
    if (invoices.length === 0) {
      dispatch(setSingleAlertObj({
        message: "Please select at least one invoice.",
        alertType: "error",
      }));
      return;
    }
    setAgencyPayoutInvoices(invoices);
  };

  const handleCloseAgencyPayout = () => setAgencyPayoutInvoices(null);

  const handleAgencyPayoutRecorded = () => {
    setAgencyPayoutInvoices(null);
    searchInvoiceList(getValues("currentPage") || 1, { ...getValues() });
  };

  const openServiceFeeEdit = async (row: invoiceTableRow, kind: ServiceFeeKind) => {
    const source = invoiceList?.content?.find((invoice) => invoice.invoiceId === row.invoiceId) ?? row;
    const result = await resolveServiceFeeAmountEdit(
      dispatch,
      allCodeTablesValuesMap,
      {
        invoiceId: Number(source.invoiceId),
        mfgInvoiceNumber: source.mfgInvoiceNumber,
        orderNumber: source.orderNumber,
        invoiceAmount: source.invoiceAmount,
        agencyId: source.agencyId,
        overrideAgencyId: source.overrideAgencyId,
      },
      kind
    );
    if (!result.ok) {
      dispatch(setSingleAlertObj({ message: result.message, alertType: "error" }));
      return;
    }
    setServiceFeeEdit(result.target);
  };

  const handleServiceFeeUpdate = async (data: any) => {
    const edit = serviceFeeEdit;
    const outcome = await saveServiceFeeAmountEdit(dispatch, data, edit?.label || "Service fee");
    dispatch(setSingleAlertObj({ message: outcome.message, alertType: outcome.ok ? "success" : "error" }));
    setServiceFeeEdit(null);
    if (outcome.ok && !outcome.unchanged) {
      searchInvoiceList(getValues("currentPage") || 1, { ...getValues() });
    }
  };

  const handleOpenPaymentInstructionDoc = async (row: invoiceTableRow | invoiceInfo) => {
    const actionResult = await dispatch(fetchDocumentById(row.paymentInstructionValue ?? ""));
    const documentData = actionResult.payload;

    if (documentData && documentData.content) {
      setPDFContent(documentData.content);
      setShowPDF({ isShow: true, noteName: "Payment Instruction - " + row.manufacturerName, fileName: "Payment Instruction_" + row.manufacturerName });
    }
  };

  const handleConfirm = (data: any) => {
    if (selectedInvoiceId) {
      dispatch(addLateFeeByInvoice({
        invoiceId: selectedInvoiceId,
        lateFee: data.lateFee,
        skipLoader: true
      })).then(() => {
        searchInvoiceList(1, { ...getValues() });
      });
      setIsPopupOpen(false);
      setSelectedInvoiceId(null);
      setLateFeeAmount(0);
    }
  };

  const actionComponent = (rowObj: invoiceTableRow): JSX.Element => {
    const originalRow = invoiceList?.content?.find(r => r.invoiceId === rowObj.invoiceId);
    const manufacturerName = originalRow?.manufacturerName || rowObj.manufacturerName;
    return (
      <>
        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Detail"
          color="info"
          size="small"
          variant="outlined"
          //CS-1453 - Added handleActionButtonClick to save highlight state
          onClick={(e?: React.MouseEvent) => {if (e) e.stopPropagation(); handleActionButtonClick(`commissionListActionButton-${rowObj.invoiceId}`,
            () => {
              const originalRow = invoiceList?.content?.find(r => r.invoiceId === rowObj.invoiceId);

              navigate("/commission_details", {
                state: {
                  invoiceId: rowObj.invoiceId,
                  invoiceAmount: priceFormatter(Number(originalRow?.invoiceAmount)) || rowObj.invoiceAmount,
                  InvoiceNumber: originalRow?.mfgInvoiceNumber || '',
                  orderNumber: originalRow?.orderNumber || '',
                  shippingCharges: originalRow?.shippingCharges || '',
                  invStatus: rowObj.invoiceStatus,
                  creditAmount: originalRow?.creditAmount || '',
                }
              });
            })}}
        >
          <ListAltOutlined fontSize="small" />
        </CustomIconButton>
        {/* CS-2187 - Same Update Invoice visibility as billing detail */}
        <CustomIconButton
          color="info"
          tooltipMessage="Update Invoice"
          tooltipPlacement="top"
          variant="outlined"
          size="small"
          style={{ display: `${hideElement(!canShowUpdateInvoice(rowObj, canAccessEditInvoiceAction))}` }}
          onClick={(e?: React.MouseEvent) => {
            if (e) e.stopPropagation();
            handleActionButtonClick(`commissionListActionButton-${rowObj.invoiceId}`, () =>
              navigate("/edit_invoice", { state: rowObj.invoiceId })
            );
          }}
        >
          <ModeOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="info"
          tooltipMessage="Activity Center"
          tooltipPlacement="top"
          variant="outlined"
          size="small"
          style={{ display: `${hideElement(!canAccessActivityCenter)}` }}
          onClick={(e?: React.MouseEvent) => {
            if (e) e.stopPropagation();
            handleActionButtonClick(`commissionListActionButton-${rowObj.invoiceId}`, () => {
              const source = originalRow || rowObj;
              navigate(buildActivityCenterPath({
                tab: "commission",
                invoiceId: rowObj.invoiceId,
                invoiceNumber: asEntityNumber(source.mfgInvoiceNumber) || asEntityNumber(rowObj.mfgInvoiceNumber),
                orderId: source.orderId || rowObj.orderId,
                orderNumber: asEntityNumber(source.orderNumber) || asEntityNumber(rowObj.orderNumber),
              }));
            });
          }}
        >
          <HistoryOutlined fontSize="small" />
        </CustomIconButton>

        <CustomIconButton
          color="warning"
          tooltipMessage="Record Credit Memo"
          tooltipPlacement="top"
          variant="outlined"
          size="small"
          style={{ display: `${hideElement(!canAccessRecordCreditMemoAction || rowObj.invoiceStatus === 'PAID' || rowObj.invoiceStatus === 'CLOSED')}` }}
          onClick={() => handleActionButtonClick(`commissionListActionButton-${rowObj.invoiceId}`,
            () => navigate("/record_credit_memo", { state: { invoiceId: rowObj.invoiceId, invNumber: rowObj.mfgInvoiceNumber, invAmt: rowObj.invoiceAmount, invDate: rowObj.mfgInvoiceDate, practice: rowObj.practiceName, agency: rowObj.agencyName, mfr: rowObj.manufacturerName } }))}
        >
          <CreditCard fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="success"
          tooltipMessage="Record Payment"
          tooltipPlacement="top"
          variant="outlined"
          size="small"
          style={{ display: `${hideElement(!canAccessAddPaymentAction || rowObj.invoiceStatus === 'PAID' || rowObj.invoiceStatus === 'CLOSED')}` }}
          onClick={() => handleActionButtonClick(`commissionListActionButton-${rowObj.invoiceId}`,
            () => navigate("/record_payment", { state: { invoiceId: rowObj.invoiceId, invNumber: rowObj.mfgInvoiceNumber, invAmt: rowObj.invoiceAmount, invDate: rowObj.mfgInvoiceDate, practice: rowObj.practiceName, agency: rowObj.agencyName, mfr: rowObj.manufacturerName, invoiceAmount: rowObj.invoiceAmount, creditAmount: rowObj.creditAmount } }))}
        >
          <AttachMoney fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="secondary"
          tooltipMessage="Record OVR Payout"
          tooltipPlacement="top"
          variant="outlined"
          size="small"
          aria-label="Record OVR Payout"
          style={{ display: `${hideElement(!canAccessCreateOVRAGYPayout || !invoiceHasOverridePayout(rowObj))}` }}
          onClick={(e?: React.MouseEvent) => {
            if (e) e.stopPropagation();
            handleActionButtonClick(`commissionListActionButton-${rowObj.invoiceId}`, () => openOverridePayout(rowObj));
          }}
        >
          <PaymentsOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="info"
          tooltipMessage="Record AGY Payout"
          tooltipPlacement="top"
          variant="outlined"
          size="small"
          aria-label="Record AGY Payout"
          sx={{ color: "#0288d1", borderColor: "#0288d1", "&:hover": { borderColor: "#0288d1", backgroundColor: "rgba(2,136,209,0.12)" } }}
          style={{ display: `${hideElement(!canAccessCreateAGYPayout || !invoiceHasAgencyPayout(rowObj))}` }}
          onClick={(e?: React.MouseEvent) => {
            if (e) e.stopPropagation();
            handleActionButtonClick(`commissionListActionButton-${rowObj.invoiceId}`, () => openAgencyPayout(rowObj));
          }}
        >
          <PaymentsOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="info"
          tooltipMessage="Edit AGY Service Fee Amount"
          tooltipPlacement="top"
          variant="outlined"
          size="small"
          aria-label="Edit AGY Service Fee Amount"
          style={{ display: `${hideElement(!canEditCommissionRate || !invoiceHasAgencyPayout(rowObj) || !invoiceCanEditServiceFee(rowObj))}` }}
          onClick={(e?: React.MouseEvent) => {
            if (e) e.stopPropagation();
            handleActionButtonClick(`commissionListActionButton-${rowObj.invoiceId}`, () => {
              void openServiceFeeEdit(rowObj, "AGENCY");
            });
          }}
        >
          <EditOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="secondary"
          tooltipMessage="Edit OVR Service Fee Amount"
          tooltipPlacement="top"
          variant="outlined"
          size="small"
          aria-label="Edit OVR Service Fee Amount"
          style={{ display: `${hideElement(!canEditCommissionRate || !invoiceHasOverridePayout(rowObj) || !invoiceCanEditServiceFee(rowObj))}` }}
          onClick={(e?: React.MouseEvent) => {
            if (e) e.stopPropagation();
            handleActionButtonClick(`commissionListActionButton-${rowObj.invoiceId}`, () => {
              void openServiceFeeEdit(rowObj, "OVERRIDE");
            });
          }}
        >
          <EditOutlined fontSize="small" />
        </CustomIconButton>
        {rowObj?.stripeConfig && (
          (
            (rowObj?.stripeConfig?.stripe_integration === 'Y' && canCreateStripePayment) ||
            (rowObj?.stripeConfig?.stripe_integration === 'N' && rowObj?.hasPaymentInstruction)
          ) && (
            <CustomIconButton
              tooltipMessage={rowObj?.stripeConfig?.stripe_integration === 'Y' ? "Pay via Stripe" : "Payment Instruction"}
              tooltipPlacement="top"
              variant="contained"
              size="small"
              color={rowObj?.stripeConfig?.stripe_integration === 'Y' ? undefined : "warning"}
              style={{
                display: `${hideElement(rowObj.invoiceStatus === 'PAID' || rowObj.invoiceStatus === 'CLOSED')}`,
                ...(rowObj?.stripeConfig?.stripe_integration === 'Y' && {
                  color: "white",
                  borderColor: theme.palette.mode === 'dark' ? "#c374ff" : "#c374ff",
                  background: theme.palette.mode === 'dark'
                    ? "linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7, #ec4899)"
                    : "linear-gradient(135deg, #4f46e5, #7c3aed, #9333ea, #db2777)",
                })
              }}
              onClick={() => handleActionButtonClick(`commissionListActionButton-${rowObj.invoiceId}`, () => {
                if (rowObj?.stripeConfig?.stripe_integration === 'Y') {
                  const outstandingAmt = Number(
                    rowObj?.actualRemainingAmount ?? rowObj?.outstandingAmount ?? rowObj?.remainingAmount ?? originalRow?.remainingAmount ?? 0
                  );
                  if (outstandingAmt > 0) {
                    setShowStripePaymentTypePopup(true);
                    setSelectedInvoiceData({
                      ...rowObj,
                      actualRemainingAmount: outstandingAmt,
                      outstandingAmount: outstandingAmt,
                    });
                  } else {
                    let errObj: SingleAlertInfo = {
                      message: "This invoice currently shows 'Unpaid,' but the outstanding amount due is 0. It was likely settled by credits or adjustments, so no payment is needed.",
                      alertType: "error"
                    };
                    dispatch(setSingleAlertObj(errObj));
                  }
                } else if (rowObj?.hasPaymentInstruction) {
                  if (rowObj?.paymentInstructionType === 'Product Document' && rowObj?.paymentInstructionValue) {
                    if (rowObj?.paymentInstructionValue?.startsWith('https://') || rowObj?.paymentInstructionValue?.startsWith('http://')) {
                      window.open(rowObj.paymentInstructionValue, '_blank');
                    } else {
                      handleOpenPaymentInstructionDoc(rowObj);
                    }
                  } else if (rowObj?.paymentInstructionType === 'External URL' && rowObj?.paymentInstructionValue) {
                    window.open(rowObj.paymentInstructionValue, '_blank');
                  }
                }
              })}
            >
              <AttachMoney fontSize="small" />
            </CustomIconButton>
          ))}
        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Close Invoice"
          color="error"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!(canAccessCloseInvoiceAction && rowObj.invoiceStatus === 'UNPAID'))}` }}
          onClick={() =>
            handleActionButtonClick(
              `commissionListActionButton-${rowObj.invoiceId}`,
              () => handleCloseInvoice(rowObj)
            )
          }
        >
          <CloseOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="success"
          tooltipMessage="Late Fee"
          tooltipPlacement="top"
          variant="outlined"
          size="small"
          onClick={() => handleClickLateFee(rowObj)}
          style={{ display: `${hideElement(!canLateFeeAction || rowObj.invoiceStatus === 'PAID' || rowObj.invoiceStatus === 'CLOSED')}` }}
        >
          <CurrencyExchangeOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Invoice Audit"
          color="warning"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!canAuditInvoice || manufacturerName === 'Migrate Manufacturer')}` }}
          onClick={() => handleActionButtonClick(`commissionListActionButton-${rowObj.invoiceId}`, () => handleClickInvoiceAudit(rowObj))}
        >
          <TrackChanges fontSize="small" />
        </CustomIconButton>
      </>
    );
  };

  const displayCheckboxConditional = (row: any) => {
    if (row?.invoiceStatus === "UNPAID") {
      return "";
    } else if (row?.invoiceStatus === "PAID" && (row?.expectedAgencyAmt === row?.totalAgencyPaid) && (row?.expectedOvrAmt === row?.totalOvrPaid)) {
      return "";
    } else {
      return checkboxComponent(row);
    }
  }

  const formattedInvoiceRows: invoiceTableRow[] | [] = useMemo(() => {
    let formatRows: invoiceTableRow[] = [];
    invoiceList?.content?.map((row) => {
      let formatRow: invoiceTableRow = {
        ...row,

        rawMfgInvoiceNumber: row.mfgInvoiceNumber, // CS-1517
        mfgInvoiceNumber: row.mfgInvoiceNumber || '',
        orderNumber: row.orderNumber || '',
        invoiceAmount: priceFormatter(Number(row.invoiceAmount)),
        creditAmount: priceFormatter(Number(row.creditAmount ? row.creditAmount : 0)),
        checkBox: displayCheckboxConditional(row),
        manufacturerName: row.manufacturerName !== 'Migrate Manufacturer' ? row.manufacturerName : '-',
        actualRemainingAmount: Number(row?.remainingAmount ?? row?.outstandingAmount ?? 0),
        outstandingAmount: Number(row?.remainingAmount ?? row?.outstandingAmount ?? 0),
      };
      formatRows.push(formatRow);
    });
    return formatRows;
  }, [invoiceList, getValues()]);

  const handleSelectAllCheckBox = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    formattedInvoiceRows?.map(row => {
      if (selectedAgencyId === row.agencyId) {
        //@ts-ignore
        setValue(`checkBox.invoice_${row.invoiceId}`, checked ? true : false);
      }
    });

    if (!checked) {
      setSelectedAgencyId(null);
    }

  }, [formattedInvoiceRows, getValues('selectAllCheckBox')])

  const practiceIdsList = useMemo(() => {
    return (
      practiceByUserRole?.map((practice) => parseInt(practice.practiceId)) || []
    );
  }, [practiceByUserRole]);

    const patientListoption = useMemo(() => {
    return (
      patientsByUserRole?.map((patient) => patient.patientId) || []
    );
  }, [patientsByUserRole]);

  // const mfgIdsList = useMemo(() => {
  //   return manufacturerInfoList?.map((mfg) => mfg.manufacturerId) || [];
  // }, [manufacturerInfoList]);

  const agencyIdsList = useMemo(() => {
    return agencyInfoList?.map((agy) => parseInt(agy.agencyId)) || [];
  }, [agencyInfoList]);

  const ovrAgencyIdsList = useMemo(() => {
    return agencyInfoList
    ?.filter(option => option.commissionType === 'Override')
    ?.map((agy) => parseInt(agy.agencyId)) || [];
  }, [agencyInfoList]);

  const searchInvoiceList = useCallback(
    (currentPage: number, obj?: SearchInvoiceInfo) => {
      let data: SearchInvoiceInfo = {
        fromDate: obj?.fromDate,
        toDate: obj?.toDate,
        commPeriod: obj?.commPeriod,
        mfgIds: obj?.mfgIds
          ? obj.mfgIds
          // : mfgIdsList?.length === 1
          //   ? mfgIdsList
            : [],
        agencyIds: obj?.agencyIds
          ? obj.agencyIds
          // : agencyIdsList?.length === 1
          //   ? agencyIdsList
            : [],
        patientIds: obj?.patientIds
          ? obj.patientIds
          // : patientListoption?.length === 1
          //   ? patientListoption
            : [],
        practiceIds: obj?.practiceIds
          ? obj.practiceIds
          // : practiceIdsList?.length === 1
          //   ? practiceIdsList
            : [],
        mfgInvoiceNumber: obj?.mfgInvoiceNumber ? obj.mfgInvoiceNumber : "",
        serialNumber: obj?.serialNumber ? obj.serialNumber : "",
        orderNumber: obj?.orderNumber ? obj.orderNumber : "",
        invoiceStatus:
          obj?.invoiceStatus && obj.invoiceStatus.length > 0
            ? obj.invoiceStatus
            : ["PAID", "PARTIAL", "UNPAID"],
        currentPage: currentPage,
        itemsPerPage: ITEMS_PER_PAGE,
        usageStatus: obj?.usageStatus ? obj.usageStatus : "",
        productIds: obj?.productIds ? obj.productIds : [],
        isDownload: false,
        excludePurchaseAndBill: true,
        v3CommStatus: obj?.v3CommStatus,
        agencyCommStatus: obj?.agencyCommStatus,
        ovrCommStatus: obj?.ovrCommStatus,
        v3ReceivedFromDate: obj?.v3ReceivedFromDate,
        v3ReceivedToDate: obj?.v3ReceivedToDate,
        agencyPaidFromDate: obj?.agencyPaidFromDate,
        agencyPaidToDate: obj?.agencyPaidToDate,
        ovrPaidFromDate: obj?.ovrPaidFromDate,
        ovrPaidToDate: obj?.ovrPaidToDate,
        ...commissionListSort.pick(obj),
      };
      setValue("currentPage", currentPage);
      setValue("sortBy", data.sortBy);
      setValue("sortOrder", data.sortOrder);
      setSearchCriteria(data);
      dispatch(fetchInvoiceList(data))
      .unwrap()
      .then((res) => {
        console.log("Invoice fetching success")
      })
      .catch((error)=>{
        console.log("Error when fetching invoices")
      })
      .finally(()=>{
        dispatch(showHideLoadingDialog(false));
      });
    },
    [practiceByUserRole, manufacturerInfoListByUserRole]
  );

  const setSearchCriteria = useCallback((data: SearchInvoiceInfo) => {
    setSearchCriteriaToSessionStorage(CAN_ACCESS_APPLICATION.INVOICE_MANAGEMENT + "SearchCriteria" + userInfo.userId, data);
  }, []);

  const handleDateRangeChange = (fromDate: DateObject | null, toDate: DateObject | null) => {
    const fromDateString = fromDate ? fromDate.format("MM/DD/YYYY") : null;
    const toDateString = toDate ? toDate.format("MM/DD/YYYY") : null;

    setValue("fromDate", fromDateString ? fromDateString : "");
    setValue("toDate", toDateString ? toDateString : "");


    setIsDateRangeChanged(true);
  };

  const handleDateChange = (date: Date | null, endDate?: Date | null) => {
    if (date && endDate) {
      const formattedStart = format(date, "MMM yy");
      const formattedEnd = format(endDate, "MMM yy");

      setValue('commPeriod', `${formattedStart} - ${formattedEnd}`);
    } else if (date && !endDate) {
      const formattedDate = format(date, "MMM yy");
      setValue('commPeriod', formattedDate);
    }
  };

  const handleClearDateChange = () => {
    setValue('commPeriod', "");
  }

  const [uploadPopupInfo, setUploadPopupInfo] = useState({
    title: "",
    fileName: "",
    filePath: "",
  });

  const showPaymentPopup = (popupName: string) => {
    if (popupName === "Import Invoices") {
      setUploadPopupInfo({
        title: popupName,
        fileName: branding?.companyNameShort+"_Template_Import_Invoice_.xlsx",
        filePath: "/branding/Bio_Template_Import_Invoice_.xlsx",
      });
    } else if (popupName === "Import Invoice Payments") {
      setUploadPopupInfo({
        title: popupName,
        fileName:  branding?.companyNameShort+"_Template_Invoice_Payments.xlsx",
        filePath: "/branding/Bio_Template_Invoice_Payments.xlsx",
      });
    } else {
      setUploadPopupInfo({
        title: popupName,
        fileName: branding?.companyNameShort + "_Template_Invoice_Service_Fee_Payments.xlsx",
        filePath: "/branding/Bio_Template_Invoice_Service_Fee_Payments.xlsx",
      });
    }
    setShowUploadsPaymentsPopup(true);
  };

  // Function to export data to Excel
  const handleExport = () => {

    const searchCriteriaFromStorage = getSearchCriteriaFromSessionStorage(CAN_ACCESS_APPLICATION.INVOICE_MANAGEMENT + "SearchCriteria" + userInfo.userId);

    if (searchCriteriaFromStorage) {

      let initData: SearchInvoiceInfo = {
        fromDate: searchCriteriaFromStorage.fromDate || "",
        toDate: searchCriteriaFromStorage.toDate || "",
        mfgIds: searchCriteriaFromStorage.mfgIds || [],
        agencyIds: searchCriteriaFromStorage.agencyIds || [],
        practiceIds: searchCriteriaFromStorage.practiceIds || [],
        mfgInvoiceNumber: searchCriteriaFromStorage.mfgInvoiceNumber || "",
        orderNumber: searchCriteriaFromStorage.orderNumber || "",
        invoiceStatus: searchCriteriaFromStorage.invoiceStatus || [],
        currentPage: getValues("currentPage"),
        itemsPerPage: ITEMS_PER_PAGE,
        patientIds: searchCriteriaFromStorage.patientIds || [],
        serialNumber: searchCriteriaFromStorage.serialNumber || "",
        usageStatus: searchCriteriaFromStorage.usageStatus || "",
        productIds: searchCriteriaFromStorage.productIds || [],
        commPeriod: searchCriteriaFromStorage.commPeriod || "",
        isDownload: true,
        excludePurchaseAndBill: false,
        v3CommStatus: searchCriteriaFromStorage.v3CommStatus || "",
        agencyCommStatus: searchCriteriaFromStorage.agencyCommStatus || "",
        ovrCommStatus: searchCriteriaFromStorage.ovrCommStatus || "",
        v3ReceivedFromDate: searchCriteriaFromStorage.v3ReceivedFromDate || "",
        v3ReceivedToDate: searchCriteriaFromStorage.v3ReceivedToDate || "",
        agencyPaidFromDate: searchCriteriaFromStorage.agencyPaidFromDate || "",
        agencyPaidToDate: searchCriteriaFromStorage.agencyPaidToDate || "",
        ovrPaidFromDate: searchCriteriaFromStorage.ovrPaidFromDate || "",
        ovrPaidToDate: searchCriteriaFromStorage.ovrPaidToDate || "",
        ...commissionListSort.pick(searchCriteriaFromStorage),
      };

      ExportToExcel(initData, columnsInvoice, dispatch, EXPORTABLE_ITEMS.COMMISSION, hiddenColumns);

    }
  }
  const selectedInvList = () => {
    const checkedList: any = getValues('checkBox') ?? {};
    const checkedIdList: number[] = Object.entries(
      checkedList
    )
      .filter(([invoiceIdString, isChecked]) => isChecked)
      .map(([invoiceIdString]) => parseInt(invoiceIdString.split("_")[1]));

    const selectedRows: invoiceInfo[] = []
    checkedIdList.forEach((invId: number) => {
      const invoice = invoiceList?.content?.find(inv => inv.invoiceId === invId);
      if (invoice) {
        selectedRows.push(invoice)
      }
    });
    return selectedRows;
  }

  const navigateRecordPayout = () => {
    const selectedRows: invoiceInfo[] = selectedInvList();
    if (selectedRows?.length > 0) {
      navigate("/recordPayout", { state: { selectedInvoices: selectedRows } });
    } else {
      let errObj: SingleAlertInfo = {
        message: "Please select at least one invoice.",
        alertType: "error"
      };
      dispatch(setSingleAlertObj(errObj));
    }
  };

  const navigateAgyRecordPayout = () => {
    const selectedRows: invoiceInfo[] = selectedInvList();
    console.log(selectedRows)
    const isOvrAgenciesFound = selectedRows.every(row => row.overrideAgencyId);
    if (!selectedRows || selectedRows?.length == 0) {
      let errObj: SingleAlertInfo = {
        message: "Please select at least one invoice.",
        alertType: "error"
      };
      dispatch(setSingleAlertObj(errObj));
    } else {
      navigate("/overrideRecordPayout", { state: { selectedInvoices: selectedRows } });
    };
  };

  const handleOrderListNavigate = (row: any) => {
    console.log(row)
    if (row?.orderNumber !== null) {
      navigate("/order_list", { state: { orderNumber: row.orderNumber } });
    };
  };

  const handleInvoiceListNavigate = (row: any) => {
    if (row?.mfgInvoiceNumber !== null) {
      navigate("/invoice_list", { state: { mfgInvoiceNumber: row.mfgInvoiceNumber } });
    };
  };

  const handleMenuClose = () => {
    setAnchorElMenu(null);
  };
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenu(event.currentTarget);
  };

  const handlev3ReceivedDateRange = (
    fromDate: DateObject | null,
    toDate: DateObject | null
  ) => {
    const fromDateString = fromDate ? fromDate.format("MM/DD/YYYY") : null;
    const toDateString = toDate ? toDate.format("MM/DD/YYYY") : null;
    setValue("v3ReceivedFromDate", fromDateString ? fromDateString : "");
    setValue("v3ReceivedToDate", toDateString ? toDateString : "");

    setIsV3DateRangeChanged(true)
  };

  const handleagencyPaidDateRange = (
    fromDate: DateObject | null,
    toDate: DateObject | null
  ) => {
    const fromDateString = fromDate ? fromDate.format("MM/DD/YYYY") : null;
    const toDateString = toDate ? toDate.format("MM/DD/YYYY") : null;
    setValue("agencyPaidFromDate", fromDateString ? fromDateString : "");
    setValue("agencyPaidToDate", toDateString ? toDateString : "");

    setIsAgencyDateRangeChanged(true)
  };

  const handleovrPaidDateRange = (
    fromDate: DateObject | null,
    toDate: DateObject | null
  ) => {
    const fromDateString = fromDate ? fromDate.format("MM/DD/YYYY") : null;
    const toDateString = toDate ? toDate.format("MM/DD/YYYY") : null;
    setValue("ovrPaidFromDate", fromDateString ? fromDateString : "");
    setValue("ovrPaidToDate", toDateString ? toDateString : "");

    setIsOvrDateRangeChanged(true)
  };

  //Unmount
  useEffect(() => () => {
    dispatch(resetInvoiceList())
  }, []);

  const totalRecords = invoiceList?.totalElements ?? 0;

  const commBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const commPanelBg = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const commHoverBg = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

  const commCardSx = {
    borderRadius: "14px",
    border: `1px solid ${commBorderColor}`,
    backgroundColor: commPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const commFilterFieldLabelSx = {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: modalTokens.textSecondary,
    mb: 0.7,
    height: "16px",
    lineHeight: "16px",
  };

  const commPrimaryBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": {
      color: "#fff",
      fontWeight: 700,
    },
    "& .MuiButton-startIcon": {
      color: "#fff",
    },
  };

  const commOutlineBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: modalTokens.accent,
    borderColor: "rgba(20,184,166,0.55)",
    backgroundColor: isDark ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
    whiteSpace: "nowrap",
    "&:hover": {
      color: "#fff",
      borderColor: modalTokens.accentHover,
      backgroundColor: modalTokens.accentHover,
      boxShadow: "0 6px 22px rgba(20,184,166,0.28)",
    },
  };

  const commFilterToggleBtnSx = {
    ...commOutlineBtnSx,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    "&:hover": {
      color: "#fff",
      borderColor: "#00B8D9",
      backgroundColor: "#00B8D9",
      boxShadow: "0 6px 22px rgba(0,184,217,0.28)",
    },
  };

  const commExcelViewBtnSx = {
    ...commPrimaryBtnSx,
    color: "#ffffff",
    backgroundColor: "#14b8a6",
    borderColor: "#14b8a6",
    boxShadow: "0 4px 18px rgba(20,184,166,0.3)",
    "& .MuiTypography-root, & .MuiButton-startIcon, & svg": { color: "#ffffff" },
    "&:hover": {
      color: "#ffffff",
      backgroundColor: "#0d9488",
      borderColor: "#0d9488",
      boxShadow: "0 8px 24px rgba(13,148,136,0.28)",
    },
    "&:focus, &:focus-visible": {
      boxShadow: "0 0 0 3px rgba(20,184,166,0.24)",
    },
  };

  const commImportBtnSx = {
    ...commPrimaryBtnSx,
    color: "#ffffff",
    backgroundColor: "#4fc3f7",
    borderColor: "#4fc3f7",
    boxShadow: "0 4px 18px rgba(79,195,247,0.3)",
    "& .MuiTypography-root, & .MuiButton-startIcon, & svg": { color: "#ffffff" },
    "&:hover": {
      color: "#ffffff",
      backgroundColor: "#38bdf8",
      borderColor: "#38bdf8",
      boxShadow: "0 8px 24px rgba(56,189,248,0.28)",
    },
    "&:focus, &:focus-visible": {
      boxShadow: "0 0 0 3px rgba(56,189,248,0.24)",
    },
  };

  const commCreateAgyBtnSx = {
    ...commPrimaryBtnSx,
    color: "#fff",
    backgroundColor: "#ef5350",
    borderColor: "#ef5350",
    boxShadow: "0 4px 18px rgba(239,83,80,0.3)",
    "& .MuiTypography-root, & .MuiButton-startIcon, & svg": { color: "#fff" },
    "&:hover": {
      color: "#fff",
      backgroundColor: "#dc2626",
      borderColor: "#dc2626",
      boxShadow: "0 8px 24px rgba(239,83,80,0.28)",
    },
    "&:focus, &:focus-visible": {
      boxShadow: "0 0 0 3px rgba(239,83,80,0.24)",
    },
  };

  const commCreateOvrBtnSx = {
    ...commPrimaryBtnSx,
    color: "#ffffff",
    backgroundColor: "#5ee59a",
    borderColor: "#5ee59a",
    boxShadow: "0 4px 18px rgba(94,229,154,0.3)",
    "& .MuiTypography-root, & .MuiButton-startIcon, & svg": { color: "#ffffff" },
    "&:hover": {
      color: "#ffffff",
      backgroundColor: "#4ade80",
      borderColor: "#4ade80",
      boxShadow: "0 8px 24px rgba(74,222,128,0.28)",
    },
    "&:focus, &:focus-visible": {
      boxShadow: "0 0 0 3px rgba(74,222,128,0.24)",
    },
  };

  const commExportBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#14B8A6",
    borderColor: "rgba(20,184,166,0.55)",
    backgroundColor: theme.palette.mode === "dark" ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
    whiteSpace: "nowrap",
    "& .MuiSvgIcon-root": {
      color: "#14B8A6",
    },
    "&:hover": {
      color: "#fff",
      borderColor: "#14B8A6",
      backgroundColor: "#14B8A6",
      boxShadow: "0 6px 22px rgba(20,184,166,0.28)",
      "& .MuiSvgIcon-root": {
        color: "#fff",
      },
    },
  };

  const commExportMenuItemBtnSx = {
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "8px",
    py: 1,
    px: 1.4,
    color: "#2dd4bf",
    borderColor: "rgba(45,212,191,0.35)",
    backgroundColor: isDark ? "rgba(20,184,166,0.13)" : "rgba(20,184,166,0.08)",
    boxShadow: "none",
    "& .MuiButton-startIcon": {
      mr: 1,
      "& svg": { fontSize: "18px" },
    },
    "&:hover": {
      color: "#fff",
      borderColor: "#2f9b8f",
      backgroundColor: "#2f9b8f",
      boxShadow: "0 10px 24px rgba(20,184,166,0.28)",
    },
    "&.Mui-disabled": {
      color: isDark ? "rgba(148,163,184,0.55)" : "rgba(100,116,139,0.7)",
      borderColor: isDark ? "rgba(148,163,184,0.18)" : "rgba(100,116,139,0.24)",
      backgroundColor: isDark ? "rgba(148,163,184,0.06)" : "rgba(100,116,139,0.06)",
    },
  };

  const commTableSx = {
    "& .MuiTableContainer-root": {
      borderRadius: "12px",
      border: "none",
      backgroundColor: "transparent",
    },
    "& table": {
      borderCollapse: "collapse",
      borderSpacing: 0,
    },
    "& .MuiTableCell-root": {
      borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
      py: 1.15,
      fontSize: "0.84rem",
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      height: "42px",
    },
    "& .MuiTableHead-root .MuiTypography-root": {
      color: "#00B8D9 !important",
      fontSize: "0.78rem",
      fontWeight: 800,
      letterSpacing: "0.02em",
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "background-color 0.18s ease, box-shadow 0.18s ease",
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: `${commHoverBg} !important`,
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      color: modalTokens.textPrimary,
      fontSize: "0.84rem",
    },
    "& .MuiIconButton-root": {
      color: isDark ? "#94a3b8" : "#475569",
      borderRadius: "8px",
      "&:hover": {
        color: modalTokens.accent,
        backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)",
      },
    },
    "& .MuiPaginationItem-root": {
      borderColor: commBorderColor,
      color: modalTokens.textSecondary,
      borderRadius: "8px",
      "&.Mui-selected": {
        color: "#fff",
        backgroundColor: modalTokens.accent,
        borderColor: modalTokens.accent,
      },
    },
  };

  return (
    <>
      <Box sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        px: { xs: 1, md: 3 },
        py: 2,
      }}>
        {/* <Header title="Invoice List" /> */}
        {showUploadPaymentsPopup && (
          <UploadPaymentsPopup
            open={showUploadPaymentsPopup}
            title={uploadPopupInfo.title}
            downloadFileName={uploadPopupInfo.fileName}
            downloadFilePath={uploadPopupInfo.filePath}
            onClose={() => {
              setShowUploadsPaymentsPopup(false);
              pagination(0);
            }}
          />
        )}
        {showStripePaymentTypePopup && (
          <StripePaymentTypePopup
            open={showStripePaymentTypePopup}
            onClose={() => setShowStripePaymentTypePopup(false)}
            invoiceData={selectedInvoiceData}
            createCheckoutSession={handleCheckout}
          />
        )}
        {showOptionPopup.show && (
          <OptionsPopup
            open={showOptionPopup.show}
            onClose={() => setShowOptionsPopup({ message: "", show: false })}
            variant={showOptionPopup.variant}
            message={<>{showOptionPopup.message}</>}
            buttons={showOptionPopup.buttons}
            title={showOptionPopup.title}
            obj={showOptionPopup.obj}
          />
        )}
        <Dialog
          open={overridePayoutInvoices !== null}
          onClose={handleCloseOverridePayout}
          fullWidth
          maxWidth="lg"
          fullScreen={isMobile}
          sx={{ zIndex: 1600 }}
          PaperProps={{ sx: paperSx(isMobile) }}
        >
          {overridePayoutInvoices !== null ? (
            <>
              <Box sx={headerSx}>
                <Box sx={iconBoxSx(modalTokens.accent)}>
                  <PaymentsOutlined sx={{ fontSize: "1.25rem", color: modalTokens.accent }} />
                </Box>
                <Box sx={{ flex: 1, pr: 4 }}>
                  <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: modalTokens.textPrimary, lineHeight: 1.3 }}>
                    Record Payout
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: modalTokens.textSecondary, mt: 0.15 }}>
                    Pay the override service fees for the selected invoices.
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseOverridePayout} size="small" sx={closeIconSx} aria-label="Close">
                  <CloseOutlined sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Box>
              <Box sx={bodySx}>
                <RecordPayoutOverrideForm
                  selectedInvoices={overridePayoutInvoices}
                  onCancel={handleCloseOverridePayout}
                  onRecorded={handleOverridePayoutRecorded}
                  overlayZIndex={1600}
                />
              </Box>
            </>
          ) : null}
        </Dialog>
        <Dialog
          open={agencyPayoutInvoices !== null}
          onClose={handleCloseAgencyPayout}
          fullWidth
          maxWidth="lg"
          fullScreen={isMobile}
          sx={{ zIndex: 1600 }}
          PaperProps={{ sx: paperSx(isMobile) }}
        >
          {agencyPayoutInvoices !== null ? (
            <>
              <Box sx={headerSx}>
                <Box sx={iconBoxSx(modalTokens.accent)}>
                  <PaymentsOutlined sx={{ fontSize: "1.25rem", color: modalTokens.accent }} />
                </Box>
                <Box sx={{ flex: 1, pr: 4 }}>
                  <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: modalTokens.textPrimary, lineHeight: 1.3 }}>
                    Record Payout
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: modalTokens.textSecondary, mt: 0.15 }}>
                    Pay the agency service fees for the selected invoices.
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseAgencyPayout} size="small" sx={closeIconSx} aria-label="Close">
                  <CloseOutlined sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Box>
              <Box sx={bodySx}>
                <RecordPayoutForm
                  selectedInvoices={agencyPayoutInvoices}
                  onCancel={handleCloseAgencyPayout}
                  onRecorded={handleAgencyPayoutRecorded}
                  overlayZIndex={1600}
                />
              </Box>
            </>
          ) : null}
        </Dialog>
        <EditCommissionPopup
          open={serviceFeeEdit !== null}
          handleClose={() => setServiceFeeEdit(null)}
          mode="commission-details"
          uiVariant="billing"
          showReasonField
          disableAmountField={false}
          disableRateField
          paymentData={serviceFeeEdit?.paymentData ?? null}
          agencyData={serviceFeeEdit?.agencyData ?? null}
          onCommissionRateUpdate={handleServiceFeeUpdate}
          zIndex={1600}
        />
        <LateFeePopup
          open={isPopupOpen}
          onClose={() => {
            setIsPopupOpen(false);
            setSelectedInvoiceId(null);
            setLateFeeAmount(0);
          }}
          onConfirm={handleConfirm}
          lateFeeReceivedAmount={lateFeeAmount}
          currency="$"
          lateFeeAmountLabel="Outstanding Late Fee"
        />
        {showPDF && (
          <PDFViewer
            isOpen={showPDF.isShow}
            onClose={() =>
              setShowPDF({ isShow: false, noteName: "", fileName: "" })
            }
            title={showPDF.noteName}
            fileName={showPDF.fileName}
            base64String={pdfContent || ""}
          />
        )}
        <Box sx={{ position: "relative", zIndex: 20, overflow: "visible" }}>
          <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
            <Card sx={filterPanelSx}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid
                  container
                  spacing={2}
                  alignItems="flex-end"
                  sx={{
                    "& .MuiInputBase-input": {
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    },
                    "& .MuiInputLabel-root": {
                      // Resting label sits inside the field, so keep it clear of the
                      // calendar/clear/arrow adornments; the floated label keeps MUI's
                      // wider limit so the full text stays visible.
                      maxWidth: "calc(100% - 60px)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      "&.MuiInputLabel-shrink": { maxWidth: "calc(133% - 32px)" },
                    },
                  }}
                >
                    {/* Row 1: Invoice ID | Order ID | Practice | Patient */}
                    <Grid item md={3} xs={12}>
                      <CustomTextField
                        controllerName="mfgInvoiceNumber"
                        control={control}
                        name="mfgInvoiceNumber"
                        label="Invoice ID"
                        placeholder="Enter invoice ID"
                        size="small"
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <CustomTextField
                        controllerName="orderNumber"
                        control={control}
                        name="orderNumber"
                        label="Order ID"
                        placeholder="Enter order ID"
                        size="small"
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <CustomMultipleDropDown
                        control={control}
                        name="practiceIds"
                        label="Practice"
                        placeholderText="Select practice"
                        options={practiceList}
                        disabled={practiceList?.length === 1}
                        size="small"
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <CustomMultipleDropDown
                        control={control}
                        name="patientIds"
                        label="Patient"
                        placeholderText="Select patient"
                        options={patientsOptions}
                        disabled={(userInfo.entityName === USER_ROLE_ENTITY_NAMES.BILLER || userInfo.entityName === USER_ROLE_ENTITY_NAMES.ACCOUNTEXECUTIVE || userInfo.entityName === USER_ROLE_ENTITY_NAMES.MANUFACTURER)? patientsOptions?.length === 0 : false}
                        size="small"
                      />
                    </Grid>
                    {/* Row 2: Manufacturer | Product | Invoice Date Range | Agency */}
                    <Grid item md={3} xs={12}>
                      <CustomMultipleDropDown
                        control={control}
                        name="mfgIds"
                        label="Manufacturer"
                        placeholderText="Select manufacturer"
                        options={manufacturesList}
                        disabled={(userInfo.entityName === USER_ROLE_ENTITY_NAMES.MANUFACTURER) ? manufacturesList?.length === 1 : false}
                        size="small"
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <CustomMultipleDropDown
                        control={control}
                        name="productIds"
                        label="Product"
                        placeholderText="Select product"
                        options={productOptions}
                        size="small"
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <CustomDateRange
                        label="Invoice Date Range"
                        control={control}
                        controllerName="dateRange"
                        onChange={handleDateRangeChange}
                        onClear={isClearDateRange}
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <CustomMultipleDropDown
                        control={control}
                        name="agencyIds"
                        label="Agency"
                        placeholderText="Select agency"
                        options={agenciesList}
                        disabled={(userInfo.entityName === USER_ROLE_ENTITY_NAMES.AGENCY || userInfo.entityName === USER_ROLE_ENTITY_NAMES.ACCOUNTEXECUTIVE) ? agenciesList?.length === 1 : false}
                        size="small"
                      />
                    </Grid>
                    {/* Row 3: Invoice Status | V3 Comm Status | AGY Comm Status | OVR Comm Status */}
                    <Grid item md={3} xs={12}>
                      <CustomMultipleDropDown
                        control={control}
                        name="invoiceStatus"
                        label="Invoice Status"
                        placeholderText="Select status"
                        options={invoicesStatusOptions}
                        size="small"
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <CustomDropdownField
                        control={control}
                        name="v3CommStatus"
                        label="V3 Pay Status"
                        placeholderText="Select V3 Svc Fee status"
                        options={v3CommissionStatusOptions}
                        size="small"
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <CustomDropdownField
                        control={control}
                        name="agencyCommStatus"
                        label="AGY Pay Status"
                        placeholderText="Select AGY Svc Fee status"
                        options={agencyCommissionStatusOptions}
                        size="small"
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <CustomDropdownField
                        control={control}
                        name="ovrCommStatus"
                        label="OVR Pay Status"
                        placeholderText="Select OVR Svc Fee status"
                        options={agencyCommissionStatusOptions}
                        size="small"
                      />
                    </Grid>
                    {/* Row 4: V3 Comm Received Date | AGY Comm Paid Date | OVR Comm Paid Date | Pay Period */}
                    <Grid item md={3} xs={12}>
                      <CustomDateRange
                        label="V3 Paid Date"
                        control={control}
                        controllerName="v3ReceivedDateRange"
                        onChange={handlev3ReceivedDateRange}
                        onClear={isV3ClearDateRange}
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <CustomDateRange
                        label="AGY Paid Date"
                        control={control}
                        controllerName="agencyPaidDateRange"
                        onChange={handleagencyPaidDateRange}
                        onClear={isAgencyClearDateRange}
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <CustomDateRange
                        label="OVR Paid Date"
                        control={control}
                        controllerName="ovrPaidDateRange"
                        onChange={handleovrPaidDateRange}
                        onClear={isOvrClearDateRange}
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <CustomMonthYearPicker
                        label="Pay Period"
                        control={control}
                        controllerName="comm"
                        name="comm"
                        onDateChange={handleDateChange}
                        onClear={handleClearDateChange}
                      />
                    </Grid>
                    {/* Row 5: Serial Number | Usage Status | Search */}
                    <Grid item md={3} xs={12}>
                      <CustomTextField
                        controllerName="serialNumber"
                        control={control}
                        name="serialNumber"
                        label="Serial Number"
                        placeholder="Enter serial number"
                        size="small"
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <CustomDropdownField
                        control={control}
                        name="usageStatus"
                        label="Usage Status"
                        placeholderText="Select usage status"
                        options={usageStatusOptions}
                        size="small"
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                    <Button
                      fullWidth
                      size="large"
                      startIcon={<SearchOutlined />}
                      sx={{ ...commPrimaryBtnSx, height: "42px", py: 0 }}
                      type="submit"
                      removeUpperCase
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Collapse>
        </Box>
        <Box mt={2} sx={{ position: "relative", zIndex: 1 }}>
          <Card sx={{ ...commCardSx, p: { xs: "14px !important", md: "18px !important" }, position: "relative", zIndex: 1 }}>
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              spacing={1.2}
            >
              <Grid item xs={12} md>
                <Typography variant='h3' fontWeight={800} color="text.primary">Finance HQ List</Typography>
              </Grid>
              <Grid item xs={12} md="auto">
                <Button
                  variant="outlined"
                  color="info"
                  onClick={toggleFilterPanel}
                  startIcon={<SearchOutlined />}
                  fullWidth
                  sx={commFilterToggleBtnSx}
                  removeUpperCase
                >
                  {showCard ? "Hide Filters" : "Show Filters"}
                </Button>
              </Grid>
              <Grid item xs={12} md="auto">
                <Button
                  color="info"
                  onClick={() => window.open("/finance_hq_sales_sheet", "_blank")}
                  startIcon={<GridOn />}
                  fullWidth
                  sx={commExcelViewBtnSx}
                  removeUpperCase
                >
                  Excel View
                </Button>
              </Grid>
              {/* {canAccessImportInvoices ?
                <Grid item xs={12} md="auto">
                  <Button
                    color="info"
                    startIcon={<AssignmentReturnedOutlined />}
                    onClick={() => showPaymentPopup("Import Invoices")}
                    fullWidth
                  >
                    Import Invoices
                  </Button>
                </Grid>
                : ""}
              {canAccessUploadPayments ?
                <Grid item xs={12} md="auto">
                  <Button
                    color="warning"
                    // startIcon={<CloudUpload />}
                    startIcon={<AssignmentReturnedOutlined />}
                    onClick={() => showPaymentPopup("Import Invoice Payments")}
                    fullWidth
                  >
                    Import INV Pmts
                  </Button>
                </Grid>
              : ""} */}
              {canAccessUploadCommisions ?
                <Grid item xs={12} md="auto">
                  <Button
                    color="info"
                    // startIcon={<CloudUpload />}
                    startIcon={<AssignmentReturnedOutlined />}
                    onClick={() => showPaymentPopup("Import Manufacturer Payments")}
                    fullWidth
                    sx={commImportBtnSx}
                    removeUpperCase
                  >
                    Import MFR Pmts
                  </Button>
                </Grid>
                : ""}
              {canAccessCreateAGYPayout ?
                <Grid item xs={12} md="auto">
                  <Button
                    color="error"
                    startIcon={<EditNote />}
                    onClick={() => navigateRecordPayout()}
                    fullWidth
                    sx={commCreateAgyBtnSx}
                    removeUpperCase
                  >
                    Create AGY Pmts
                  </Button>
                </Grid>
                : ""}
              {canAccessCreateOVRAGYPayout ?
                <Grid item xs={12} md="auto">
                  <Button
                    color="secondary"
                    startIcon={<EditNote />}
                    onClick={() => navigateAgyRecordPayout()}
                    fullWidth
                    sx={commCreateOvrBtnSx}
                    removeUpperCase
                  >
                    Create OVR Pmts
                  </Button>
                </Grid>
                : ""}
              {/* {
                canExportCommissionList && <Grid item xs={12} md="auto">
                  {!isMobile &&
                    (<Button
                      id="customized-button"
                      aria-controls={Boolean(anchorElMenu) ? "customized-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={Boolean(anchorElMenu) ? "true" : undefined}
                      variant="outlined"
                      disableElevation
                      onClick={handleMenuClick}
                      startIcon={<ExitToApp />}
                      endIcon={<Box
                        component="span"
                        sx={{
                          display: "flex",
                          transition: "transform 0.3s ease",
                          transform: anchorElMenu ? "rotate(180deg)" : "rotate(0deg)",
                          padding: "2px"
                        }}
                      >
                        <KeyboardArrowDown />
                      </Box>}
                    >
                      Export
                    </Button>
                    )
                  }
                </Grid>
              } */}
              {/* CS-1294 - Start */}
              {canExportCommissionList && (
                <Grid item xs={12} md="auto">
                  {!isMobile && (
                    <Button
                      id="customized-button"
                      aria-controls={Boolean(anchorElMenu) ? "customized-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={Boolean(anchorElMenu) ? "true" : undefined}
                      variant="outlined"
                      disableElevation
                      onClick={handleMenuClick}
                      startIcon={<ExitToApp />}
                      sx={commExportBtnSx}
                      removeUpperCase
                      endIcon={
                        <Box
                          component="span"
                          sx={{
                            display: "flex",
                            transition: "transform 0.3s ease",
                            transform: anchorElMenu ? "rotate(180deg)" : "rotate(0deg)",
                            padding: "2px",
                          }}
                        >
                          <KeyboardArrowDown />
                        </Box>
                      }
                    >
                      Export
                    </Button>
                  )}
                </Grid>
              )}

              <Grid item xs={12} md="auto">
                <Box>
                  {/* {canExportCommissionList && isMobile && (
                    <Box position="absolute" mt={1}>
                      <Button
                        color="success"
                        variant="text"
                        onClick={(event) => handleMenuClick(event)}
                        startIcon={<DownloadOutlined fontSize="small" />}
                      >
                        Export
                      </Button>
                    </Box>
                  )} */}

                  <StyledMenu
                    id="customized-menu"
                    MenuListProps={{ "aria-labelledby": "customized-button" }}
                    anchorEl={anchorElMenu}
                    open={Boolean(anchorElMenu)}
                    onClose={handleMenuClose}
                    slotProps={{
                      paper: {
                        sx: {
                          width: "260px",
                          maxWidth: "90vw",
                          mt: 0.75,
                          p: 0.75,
                          borderRadius: "12px",
                          border: `1px solid ${isDark ? "rgba(0,184,217,0.3)" : "rgba(0,184,217,0.22)"}`,
                          backgroundColor: isDark ? "#141720" : "#ffffff",
                          backgroundImage: "none",
                          boxShadow: isDark
                            ? "0 18px 42px rgba(0,0,0,0.45)"
                            : "0 14px 32px rgba(15,23,42,0.14)",
                        },
                      },
                    }}
                  >
                    <Box sx={{ px: 1, mb: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<CloudDownload />}
                        onClick={() => handleExport()}
                        disabled={totalRecords > 1000}
                        sx={commExportMenuItemBtnSx}
                        removeUpperCase
                      >
                        Export Search List
                      </Button>
                    </Box>

                    {/* <Box sx={{ px: 1, mb: 1 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        startIcon={<CloudDownload />}
                        disabled={totalRecords > 1000}
                        sx={{
                          py: 1,
                          justifyContent: "flex-start",
                          textTransform: "none",
                          fontSize: "1rem",
                        }}
                      >
                        Export Details List
                      </Button>
                    </Box> */}

                    {totalRecords > 1000 && (
                      <Box
                        sx={{
                          mx: 1,
                          p: 1.5,
                          mt: 1,
                          backgroundColor: "warning.light",
                          borderRadius: 1,
                          color: "warning.contrastText",
                        }}
                      >
                        <Typography variant="body2">
                          Export is available only when the list contains 1,000 records or fewer.
                          Maximum export size: 1000 records.
                        </Typography>
                      </Box>
                    )}
                  </StyledMenu>
                </Box>
              </Grid>

              {/* CS-1294 - End */}
            </Grid>
            <Table
              sx={commTableSx}
              columns={columnsInvoice}
              rows={formattedInvoiceRows || []}
              emptyText="No Invoice(s) Found"
              totalPages={invoiceList?.totalPages}
              pageNumber={pagination}
              hiddenColumns={hiddenColumns}
              actionComponent={(row: invoiceTableRow) => actionComponent(row)}
              totalElementText={"Records: " + (invoiceList?.totalElements ? invoiceList?.totalElements : "0")}
              currentPageNumber={getValues("currentPage") ? getValues("currentPage") - 1 : 1}
              showMenu={true}
              sortableColumns={commissionListSort.sortableColumns}
              serverSideSorting
              onSortChange={handleTableSort}
              controlledSortColumn={commissionListSort.controlledColumn(getValues("sortBy"))}
              controlledSortOrder={getValues("sortOrder") ?? null}
              uniqueValue="invoiceId"
              currentPageKey="commissionList"
              activeButtonId={activeButtonId}
              // CS-1555 - Start
              onRowClick={handleRowNavigateToDetails}
              clickableCards={true}
              // CS-1555 - End
            />
          </Card>
        </Box>
      </Box>
    </>
  );
};

export default CommissionList;
